import { NgIf } from '@angular/common';
import { Component, HostBinding, inject, Input, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatMiniFabButton } from '@angular/material/button';
import { MatDialogConfig } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { ObjektHelper } from '@app/api/core/utils/helper/objekt-helper';
import { DialogInputData } from '@app/shared/services/ui/dialog/dialog.outdated.types';
import { DialogStatus } from '@app/shared/services/ui/dialog/dialog.types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ObjektBase, ObjektTyp, Person, PersonResult } from '@o30/entities';
import { take } from 'rxjs/operators';
import { DynamicComponent } from '../../../dynamic-form/dynamic-component.model';
import { ValidatorConfig } from '../../../dynamic-form/validator-config.model';
import { FormatPersonPipe } from '../../../pipes/formatperson.pipe';
import { DataService } from '../../../services/data/data.service';
import { DialogService } from '../../../services/ui/dialog/dialog.service';

@UntilDestroy()
@Component({
  selector:    'app-beitragszahler',
  templateUrl: './beitragszahler.component.html',
  styleUrls:   ['./beitragszahler.component.scss'],
  standalone:  true,
  imports:     [
    NgIf,
    MatIcon,
    MatMiniFabButton,
    FormatPersonPipe
  ]
})

export class BeitragszahlerComponent implements DynamicComponent,
                                                OnInit {
  dataService = inject(DataService);
  dialogService = inject(DialogService);

  data: ObjektBase;
  objektTyp: ObjektTyp = ObjektTyp.BEITRAGSZAHLER;

  person: Person;
  editable: boolean;
  private formControl: UntypedFormControl;

  @Input() styleClass: string;

  @HostBinding('class') get styleClasses(): string {
    let c = 'o30-form-control';
    if (this.styleClass) {
      c += ' ' + this.styleClass;
    }
    return c.trim();
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--DynamicComponent implementation--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  dncInit(formControl: UntypedFormControl, validators: ValidatorConfig) {
    this.formControl = formControl;
  }

  focus() {
    // empty override
  }

  markAsTouched() {
    // empty override
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--lifecycle hooks--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  ngOnInit(): void {
    if (this.formControl.value) {
      this.data = JSON.parse(JSON.stringify(this.formControl.value));
    }
    this.formControl.valueChanges.pipe(
        untilDestroyed(this))
      .subscribe((newValue) => {
        this.data = newValue;
      });
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--getters/setters--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  get personId() {
    return this.data ? this.data.id : null;
  }

  set personId(id: number) {
    if (!this.data) {
      this.data = ObjektHelper.toObjektBase();
    }
    this.data.id = id;
    this.formControl.setValue(this.data);
  }

  /*,.-"^"-.,.-"^"-.,.-"^"-.,.-"^"-.,[--events--],.-"^"-.,.-"^"-.,.-"^"-.,.-"^ */

  onSelectPerson(): void {
    const config: MatDialogConfig = {
      data: {
        title:                               'Suche nach Personen',
        enableNewPerson:                     true,
        enableNewPersonManualRechtsstellung: true
      }
    } as MatDialogConfig<DialogInputData>;
    this.dialogService.displaySearchDialog<PersonResult>('person', config)
      .afterClosed()
      .pipe(
        take(1),
        untilDestroyed(this))
      .subscribe(_result => {
        if (_result?.data) {
          this.personId = _result.data.id;
        } else if (_result?.neu) {
          this.dialogService.displayPersonErfassenDialog({
              objektTyp:      ObjektTyp.BEITRAGSZAHLER,
              newPersonIsJur: _result.newPersonIsJur,
              filledMembers:  _result.filledMembers
            })
            .afterClosed()
            .pipe(
              take(1),
              untilDestroyed(this))
            .subscribe(newPerson => {
              if (newPerson?.data) {
                this.personId = newPerson.data.id;
              }
            });
        }
      });
  }

  onEditPerson() {
    if (this.enabled) {
      this.dialogService.displayPersonErfassenDialog({
          objektTyp: ObjektTyp.BEITRAGSZAHLER,
          objId:     this.data.id
        })
        .afterClosed()
        .pipe(
          take(1),
          untilDestroyed(this))
        .subscribe(_dialogErfassenResult => {
          if (_dialogErfassenResult?.statusId === DialogStatus.SAVE_SUCCESS) {
            this.formControl.setValue(this.data);
          }
        });
    }
  }

  get enabled() {
    return !!this.personId && this.editable;
  }
}
